import { Fill, Stroke, Style, Text } from "ol/style";
import { Feature } from "ol";
import { Geometry } from "ol/geom";
import type { TLegendItem } from "@services/fieldsService";

type TUpdateFeatureStyleOptions = {
  selected?: boolean;
  /** Whole-field boundary polygons get a visible white border; heatmap and
   * productivity zone polygons must keep the 0.5 hairline. */
  border?: boolean;
};

const updateFeatureStyle = (
  feature: Feature<Geometry>,
  options?: TUpdateFeatureStyleOptions
) => {
  const style = (feature.getStyle() as unknown as Style) ?? new Style();
  const selected = options?.selected;
  let name = feature.get("name");
  if (name !== undefined) {
    style.setText(
      new Text({
        padding: [4, 7, 4, 7],
        textAlign: "center",
        textBaseline: "ideographic",
        font: "12px/1.2 Nunito Sans",
        text: feature.get("name"),
        fill: new Fill({ color: selected ? "#494C5B" : "#ffffff" }),
        backgroundFill: new Fill({ color: selected ? "#ffffff" : "#494C5B" }),
        offsetX: 0,
        offsetY: 0,
        placement: "point",
        maxAngle: 0.7853981633974483,
        overflow: false,
        rotation: 0,
      })
    );
  }

  const fillColor = feature.get("fill");
  if (fillColor !== undefined) {
    const fill = style.getFill() ?? new Fill();
    fill.setColor(fillColor);
    style.setFill(fill);
  }

  const strokeColor = feature.get("stroke");
  if (strokeColor !== undefined) {
    const stroke = style.getStroke() ?? new Stroke();
    if (options?.border) {
      stroke.setWidth(selected ? 3 : 2);
      stroke.setColor("#ffffff");
    } else {
      stroke.setWidth(0.5);
      stroke.setColor(selected ? "#ffffff" : strokeColor);
    }
    style.setStroke(stroke);
  }

  feature.setStyle(style);
};

/**
 * Color yield cells on the given legend instead of the colors their own
 * response carried, and style them. Each yield response stretches its legend
 * over its own values, so the same color means a different yield on two maps;
 * colored on one legend, two maps of a field compare color for color.
 *
 * Classes are cut at each class's rounded `start`. Color BOTH maps through
 * here: if one keeps the server's colors, a cell on a class edge can differ
 * between two identical values.
 *
 * Returns the legend with each class's share of these cells.
 */
export const colorByLegend = (
  features: Feature<Geometry>[],
  legend: TLegendItem[]
): TLegendItem[] => {
  const counts = legend.map(() => 0);
  features.forEach((feature) => {
    const value = feature.get("yield");
    if (legend.length > 0 && typeof value === "number" && Number.isFinite(value)) {
      let i = 0;
      while (i < legend.length - 1 && value >= legend[i + 1].start) i++;
      counts[i]++;
      feature.set("fill", legend[i].color, true);
      feature.set("stroke", legend[i].color, true);
    }
    updateFeatureStyle(feature);
  });
  const total = counts.reduce((sum, n) => sum + n, 0);
  return legend.map((item, i) => ({
    ...item,
    percent: total > 0 ? Math.round((counts[i] / total) * 100) : 0,
  }));
};

export default updateFeatureStyle;
